<template>
    <div class="services-block services">
        <preloader :visible="preloader" />
        <v-server-table :name="name" ref="servicesTable" :columns="columns" :options="options" @loading="onLoading" @loaded="onLoaded" class="custom-table-modifier">
            <div v-if="canFilter" slot="beforeTable"><v-expansion-panels>
                <v-expansion-panel>
                    <v-expansion-panel-header hide-actions>
                        <span class="header">Advanced filters</span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-row>
                            <v-col md="12" lg="4" cols="4">
                                <v-select :items="modalitiesList" v-model="options.filters.types" chips deletable-chips multiple clearable
                                          :return-object="false" label="Modalities"></v-select>
                            </v-col>
                            <v-col md="12" lg="8" cols="8">
                                <v-combobox :items="holterCodesList" v-model="options.filters.codes" chips deletable-chips multiple clearable
                                            :return-object="false" label="Primary Indication"></v-combobox>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12"><div class="pull-right"><v-btn @click="applyFilters">Apply</v-btn></div></v-col>
                        </v-row>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels></div>
            <div v-if="canSelect" slot="filter__cb"><input type="checkbox" class="form-control" v-model="selectedAll" @change="markAll()"></div>
            <template v-if="canSelect" v-slot:cb="ctx"><input type="checkbox" class="form-control" v-model="selected[ctx.row.id]"></template>
            <template v-slot:id="ctx"><a :href="'/service/' + ctx.row.id">{{ ctx.row.id }}</a></template>
            <template v-slot:device="ctx"><div v-if="ctx.row.device"><a :href="'/device/' + ctx.row.device.id">{{ ctx.row.device.serial }}</a></div></template>
            <template v-slot:name="ctx">
                <div class="pull-right">
                    <v-menu v-if="hasDocuments(ctx.row)">
                        <template v-slot:activator="{ on, attrs }">
                            <div v-html="getServiceStatus(ctx.row)" v-bind="attrs" v-on="on"></div>
                        </template>
                        <v-list>
                            <v-list-item v-for="(i, x) in ctx.row.documents" :key="x">
                                <v-list-item-title><a :href="`/document/${i.id}/download`" class="padding-left-10">{{ i.source_name }}</a></v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                    <div v-else v-html="getServiceStatus(ctx.row)"></div>
                </div>
                <div data-toggle="tooltip" data-placement="top" :title="getServiceTooltip(ctx.row)" class="max-width">
                    <div v-if="getServiceDescription(ctx.row)">
                        <div class="text--primary body-1">{{ getServiceDescription(ctx.row) }}</div>
                        <div class="text--secondary body-2" v-if="getServiceName(ctx.row)">{{ getServiceName(ctx.row) }}</div>
                    </div>
                    <div v-else class="text--primary body-1">{{ getServiceName(ctx.row) }}</div>
                </div>
            </template>
            <template v-slot:starts="ctx"><span v-html="momentVue(ctx.row.starts_at).format(dateFormat).replace(/\s/,'<br>')" /></template>
            <template v-slot:ends="ctx"><span v-html="momentVue(ctx.row.finishes_at).format(dateFormat).replace(/\s/,'<br>')" /></template>
            <template v-slot:updated="ctx">
                <span :style="{color: getColor(momentDiff(ctx.row.last_updated))}" v-if="ctx.row.last_updated">
                  <b>{{ momentFromNow(ctx.row.last_updated) }}</b>
                </span>
                <span v-if="!ctx.row.last_updated" style="color:#fb434a"><b>Never</b></span>
            </template>
            <template v-slot:actions="ctx" class="actions-bts" style="text-align: right; padding-bottom: 5px">
                <a class="mr-2 mb-2 btn btn-sm btn-danger" v-on:click="tryDelete(ctx.row.id)" v-if="canDelete"><i class="icmn-bin" aria-hidden="true" /></a>
                <a :href="'/service/' + ctx.row.id + '/edit'" class="mr-2 mb-2 btn btn-sm btn-primary" v-if="canEdit"><i class="icmn-pencil3" aria-hidden="true" /></a>
                <a class="mr-2 mb-2 btn btn-sm" v-on:click="showShare(ctx.row)" v-if="canShare"><i class="icmn-share2" aria-hidden="true"></i></a>
            </template>
        </v-server-table>
    </div>
</template>

<script>
import Preloader from "./blocks/preloader";
import { CvService } from '../cv-service.js';
import { CvConsts } from '../cv-consts.js';

export default {
    components: { Preloader },
    props: {
        route: {
            type: String
        },
        dateFormat: {
            type: String,
            default: 'MM-DD-YYYY'
        },
        settings: {
            type: String,
        },
        canDelete: {
            type: Boolean,
            default: false
        },
        canEdit: {
            type: Boolean,
            default: false
        },
        canShare: {
            type: Boolean,
            default: false
        },
        canSelect: {
            type: Boolean,
            default: false
        },
        canFilter: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            preloader: false,
            message: '',
            modalitiesList: CvConsts.Modalities,
            holterCodesList: CvConsts.HolterCodes,
            selectedAll: false,
            selected: {},
            filteringParams: {},  /* filteringParams is required for external requests filtering.  Do not optimize.  */
            columns: (this.canSelect ? [ 'cb' ] : [])
                .concat([ 'id', 'device', 'name', 'starts', 'ends', 'updated' ])
                .concat(this.canEdit || this.canDelete || this.canShare ? [ 'actions' ] : []),
            options: {
                saveState: true,
                columnsClasses: {
                    name: 'ignore-custom-table-modifier',
                    device: 'hide-column-tablet'
                },
                filterByColumn: true,
                filters: {
                    types: [],
                    codes: [],
                },
                debounce: 1000,
                requestFunction: (data) => {
                    const item = this.$children[1]; // $refs can't be used here as it is not populated until reder happens.
                    if (!('query' in this.filteringParams)) {
                        const v = item.storage.getItem(`${item.stateKey}.filters`);
                        if (v) this.options.filters = JSON.parse(v);
                    }

                    const customFilters = this.canFilter ? Object.fromEntries(Object.entries(this.options.filters).filter(([k,v]) => (v != null && v !== ''))) : {};
                    item.storage.setItem(`${item.stateKey}.filters`, JSON.stringify(customFilters));
                    data['query'] = 'query' in data ? {
                        ...data.query,
                        ...customFilters
                    } : customFilters;
                    this.filteringParams = data;
                    return axios.get(this.route, { params: data });
                },
                dateColumns:[ 'starts', 'ends', 'updated' ],
                dateFormat: this.dateFormat,
                datepickerOptions: {
                    showDropdowns: true,
                    autoUpdateInput: true,
                    opens: 'left',
                    locale: {
                        format: '   MM/DD/YYYY   '
                    }
                },
                orderBy: {
                    ascending: false,
                },
                sortable: [ 'id', 'starts', 'ends', 'updated' ],
                filterable: [ 'id', 'device', 'name', 'starts', 'ends' ],
                perPage: 30,
                pagination: {
                    chunk:30,
                    dropdown:false
                },
                headings: {
                    cb: 'x',
                    id: 'ID',
                    device: 'Device',
                    name: 'Name',
                    starts: 'Start',
                    ends: 'End',
                    updated: 'Updated',
                    actions: 'Actions',
                },
            }
        }
    },
    methods: {
        getServiceDescription(service) {
            return CvService.getServiceName(service);
        },
        getServiceName(service) {
            return service.name;
        },
        getServiceStatus(service) {
            return `<span>${CvService.getServiceStatus(service)}</span>`;
        },
        getServiceTooltip(service) {
            const d = this.getServiceDescription(service);
            const n = this.getServiceName(service);
            return d ? (n ? `${d}\n\n${n}` : d) : (n ? n : '');
        },
        hasDocuments(service) {
            return Array.isArray(service.documents) && service.documents.length > 0;
        },
        momentVue(...args) {
            return moment.utc(...args).local();
        },
        momentDiff(timestamp) {
            return moment().diff(moment.utc(timestamp), 'minutes');
        },
        momentFromNow(timestamp) {
            return moment.utc(timestamp).fromNow();
        },
        getColor(minutes) {
            const settings = JSON.parse(this.settings);
            let color = null;
            for (const i in settings) {
                if (settings[i].from <= minutes) {
                    color = settings[i].color;
                }
            }
            return color;
        },
        onLoading(e) {
            this.preloader = true;
        },
        onLoaded(e) {
            this.selected = Object.fromEntries(this.$refs.servicesTable.data.map((i) => { return [ i.id, false ]; }));
            this.preloader = false;
        },
        applyFilters() {
            this.$refs.servicesTable.getData();
        },

        showShare(item) {
            $('#share-service-dialog')
                .data('options', { serviceId: item.id })
                .modal({ backdrop: 'static', keyboard: false });
        },

        tryDelete(id) {
            this.$swal.fire({
                title: 'Are you sure?',
                text: 'You would not be able to recover this study!',
                icon: 'warning',
                confirmButtonText: 'Delete',
                showCloseButton: true,
                showCancelButton: true,
                cancelButtonText: 'Cancel'
            }).then((result) => {
                if (result.isConfirmed) {
                    this.deleteItem(id).then(() => {
                        this.$refs.servicesTable.getData();
                    });
                }
            });
        },
        tryDeleteSelected() {
            const self = this;
            const items = Object.keys(Object.fromEntries(Object.entries(this.selected).filter(([k, v]) => v)));
            if (!Array.isArray(items) || items.length < 1) return;

            this.$swal.fire({
                title: 'Are you sure?',
                text: 'You would not be able to recover these studies!',
                icon: 'warning',
                confirmButtonText: 'Delete',
                showCloseButton: true,
                showCancelButton: true,
                cancelButtonText: 'Cancel'
            }).then((result) => {
                if (result.isConfirmed) {
                    Promise.all(items.map((i) => self.deleteItem(i))).then(value => {
                        self.$refs.servicesTable.getData();
                    });
                }
            });
        },
        deleteItem(id) {
            const self = this;
            return axios.delete(`/service/${id}`).then(response => {
                if (response.data.error) {
                    self.toast(response.data.error, 'error');
                } else {
                    self.toast(response.data.success);
                }
            }).catch(e => {
                console.log(e);
                self.toast('Service was not deleted because of server error.', 'error');
            });
        },

        toast(message, type) {
            const self = this.$swal;
            self.fire({
                text: message,
                icon: type || 'success',
                toast: true,
                showConfirmButton: false,
                position: 'top-end',
                timer: 1500,
                didOpen: (toast) => {
                    toast.addEventListener('mouseenter', self.stopTimer)
                    toast.addEventListener('mouseleave', self.resumeTimer)
                }
            });
        },

        markAll() {
            for (let i in this.selected) this.selected[i] = this.selectedAll;
        },
    },
    computed: {
        name: function () {
            return this.route.replaceAll('/', '_');
        }
    }
}
</script>

<style>
.patients-block .actions-bts > a {
    margin: 2px 0;
}

.popover {
    max-width: unset;
}
.tooltip {
    display: block !important;
    z-index: 10000;
}
.tooltip:focus {
    outline: none;
}
.tooltip .tooltip-inner {
    background: black;
    color: white;
    border-radius: 16px;
    padding: 5px 10px 4px;
    max-width: unset;
}
.tooltip .tooltip-arrow {
    width: 0;
    height: 0;
    border-style: solid;
    position: absolute;
    margin: 5px;
    border-color: black;
    z-index: 1;
}
.tooltip[x-placement^="top"] {
    margin-bottom: 5px;
}
.tooltip[x-placement^="top"] .tooltip-arrow {
    border-width: 5px 5px 0 5px;
    border-left-color: transparent !important;
    border-right-color: transparent !important;
    border-bottom-color: transparent !important;
    bottom: -5px;
    left: calc(50% - 5px);
    margin-top: 0;
    margin-bottom: 0;
}
.tooltip[x-placement^="bottom"] {
    margin-top: 5px;
}
.tooltip[x-placement^="bottom"] .tooltip-arrow {
    border-width: 0 5px 5px 5px;
    border-left-color: transparent !important;
    border-right-color: transparent !important;
    border-top-color: transparent !important;
    top: -5px;
    left: calc(50% - 5px);
    margin-top: 0;
    margin-bottom: 0;
}
.tooltip[x-placement^="right"] {
    margin-left: 5px;
}
.tooltip[x-placement^="right"] .tooltip-arrow {
    border-width: 5px 5px 5px 0;
    border-left-color: transparent !important;
    border-top-color: transparent !important;
    border-bottom-color: transparent !important;
    left: -5px;
    top: calc(50% - 5px);
    margin-left: 0;
    margin-right: 0;
}
.tooltip[x-placement^="left"] {
    margin-right: 5px;
}
.tooltip[x-placement^="left"] .tooltip-arrow {
    border-width: 5px 0 5px 5px;
    border-top-color: transparent !important;
    border-right-color: transparent !important;
    border-bottom-color: transparent !important;
    right: -5px;
    top: calc(50% - 5px);
    margin-left: 0;
    margin-right: 0;
}
.tooltip.popover .popover-inner {
    background: #f9f9f9;
    color: black;
    padding: 15px;
    border-radius: 5px;
    box-shadow: 0 5px 30px rgba(0,0,0, .1);
}
.tooltip.popover .popover-arrow {
    border-color: #c8cfdb;
}
.tooltip[aria-hidden='true'] {
    visibility: hidden;
    opacity: 0;
    transition: opacity .15s, visibility .15s;
}
.tooltip[aria-hidden='false'] {
    visibility: visible;
    opacity: 1;
    transition: opacity .15s;
}
.tooltip-custom-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 2px 0;
    min-width: 200px;
}
.tooltip-custom-row .btn {
    margin-left: 5px;
}
</style>
